import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideFunnelSampleContentInitializer } from 'shared/src/services/funnel-sample-content.service';
import { provideStatsigInitializer } from '../../../funnel/src/app/app.config';
import { FunnelFlowService } from '../../../funnel/src/app/services/funnel-flow.service';
import { PaywallService } from '../../../funnel/src/app/services/paywall.service';
import { QuizRouteReuseStrategy } from '../../../funnel/src/app/services/quiz-route-reuse-strategy';
import { KindleFunnelFlowService } from '../../../kindle-funnel/src/app/services/kindle-funnel-flow.service';
import { KindlePaywallService } from '../../../kindle-funnel/src/app/services/kindle-paywall.service';
import { AnalyticsService } from '../../../shared/src/services/analytics.service';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../../../shared/src/services/local-storage.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';
import { StripeService } from '../../../shared/src/services/stripe.service';
import { UserInfoService } from '../../../shared/src/services/user-info.service';
import {
  HOST_PROJECT_NAME,
  PROJECT_NAME,
  ProjectName,
  ProjectNames,
} from '../../../shared/src/tokens';
import { routes } from './app.routes';
import { ApplovinFunnelFlowService } from './services/applovin-funnel-flow.service';
import { Router } from '@angular/router';
import { FunnelSampleContentService } from '../../../shared/src/services/funnel-sample-content.service';
import { CustomizerSettingsService } from '../../../shared/src/services/customizer-settings.service';

function getExplicitFunnelVariant(): ProjectName | undefined {
  const funnelParam = new URLSearchParams(window.location.search).get('funnel');

  if (funnelParam === '1') {
    return ProjectNames.FUNNEL;
  }

  if (funnelParam === 'kindle') {
    return ProjectNames.KINDLE_FUNNEL;
  }

  if (funnelParam === '0') {
    return ProjectNames.APPLOVIN_FUNNEL;
  }

  return undefined;
}

function resolvePersistedFunnelVariant(storage: LocalStorageService): ProjectName {
  const explicitVariant = getExplicitFunnelVariant();

  if (explicitVariant) {
    storage.set(LocalStorageKey.funnelVariant, explicitVariant);
    return explicitVariant;
  }

  return storage.get<ProjectName>(LocalStorageKey.funnelVariant) ?? ProjectNames.APPLOVIN_FUNNEL;
}

function resolveProjectName(storage: LocalStorageService): ProjectName {
  return resolvePersistedFunnelVariant(storage);
}

function resolveFunnelFlowService(
  router: Router,
  localStorageService: LocalStorageService,
  userInfo: UserInfoService,
  analyticsService: AnalyticsService,
  stripeService: StripeService,
  sampleContentService: FunnelSampleContentService,
  localeId: string,
  projectName: ProjectName,
  themeService: CustomizerSettingsService,
  newsbreakService: NewsbreakService
): FunnelFlowService {
  const deps = [
    router,
    localStorageService,
    userInfo,
    analyticsService,
    stripeService,
    sampleContentService,
    localeId,
    projectName,
    themeService,
    newsbreakService,
  ] as const;

  switch (resolvePersistedFunnelVariant(localStorageService)) {
    case ProjectNames.FUNNEL:
      return new FunnelFlowService(...deps);
    case ProjectNames.KINDLE_FUNNEL:
      return new KindleFunnelFlowService(...deps);
    default:
      return new ApplovinFunnelFlowService(...deps);
  }
}

function resolvePaywallService(
  localStorageService: LocalStorageService,
  flowService: FunnelFlowService,
  sampleContentService: FunnelSampleContentService
): PaywallService {
  return resolvePersistedFunnelVariant(localStorageService) === ProjectNames.KINDLE_FUNNEL
    ? new KindlePaywallService(flowService, sampleContentService)
    : new PaywallService(flowService);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(),
    provideZoneChangeDetection(),
    provideAnimations(),
    {
      provide: PROJECT_NAME,
      useFactory: resolveProjectName,
      deps: [LocalStorageService],
    },
    {
      // PROJECT_NAME follows whichever flow `?funnel=` selected, so it stops identifying this
      // deployment. This one does not move: it is what marks a visitor as AppLovin traffic.
      provide: HOST_PROJECT_NAME,
      useValue: ProjectNames.APPLOVIN_FUNNEL,
    },
    {
      provide: RouteReuseStrategy,
      useClass: QuizRouteReuseStrategy,
    },
    provideFunnelSampleContentInitializer(),
    provideStatsigInitializer(),
    {
      // `funnel=1` forces the base funnel flow with no AppLovin-specific overrides,
      // `funnel=kindle` forces the Kindle funnel flow.
      provide: FunnelFlowService,
      useFactory: resolveFunnelFlowService,
      deps: [
        Router,
        LocalStorageService,
        UserInfoService,
        AnalyticsService,
        StripeService,
        FunnelSampleContentService,
        LOCALE_ID,
        PROJECT_NAME,
        CustomizerSettingsService,
        NewsbreakService,
      ],
    },
    {
      // The Kindle flow comes with its own paywall variant.
      provide: PaywallService,
      useFactory: resolvePaywallService,
      deps: [LocalStorageService, FunnelFlowService, FunnelSampleContentService],
    },
  ],
};
